import React, { useEffect, useState } from "react";
import moment from "moment";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCalendar,
  LuClock,
  LuIndianRupee,
  LuScale,
  LuWallet,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import { TableEmptyRow, TableLoadingRow } from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorTextClass,
  fieldLabelClass,
  filterGridClass,
  formFieldIconClass,
  summaryCardTitleClass,
  summaryCardValueClass,
  tableHeadCellClass,
} from "../ui/styles";
import {
  getEmployee,
  toggleCreateModal,
} from "../../features/salaries/salariesSlice";
import { GET } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";
import { IFormInput } from "../../types/salaries";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type Props = {
  handleCreateSalaries: (data: IFormInput) => void;
};

const CreateForm = ({ handleCreateSalaries }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const employee = useAppSelector(getEmployee);

  const [isStep2Open, setIsStep2Open] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingSalaries, setIsLoadingSalaries] = useState<boolean>(false);
  const [pendingSalaries, setPendingSalaries] = useState<
    { from_date: string; to_date: string }[]
  >([]);
  const [orderAmount, setOrderAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalHours, setTotalHours] = useState<number>(0);
  const [totalWithdrawal, setTotalWithdrawal] = useState<number>(0);
  const [lastTotalWithdrawal, setLastTotalWithdrawal] = useState<number>(0);
  const [lastTotalAmount, setLastTotalAmount] = useState<number>(0);
  const [remainingPayment, setRemainingPayment] = useState<number>(0);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (id) {
      setIsLoading(true);
      await handleCreateSalaries({
        ...data,
        employee_id: parseInt(id),
        status: 0,
      });
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
  };

  const handleChangeEmployee = async (id: string) => {
    setIsLoadingSalaries(true);

    await GET(`/v1/admin/salaries/employees/${id}/pending-salary`, {})
      .then(async (response: any) => {
        if (response.data.status === "success") {
          setPendingSalaries(response.data.data || []);
        } else {
          setPendingSalaries([]);
        }
      })
      .catch((error: any) => {
        setPendingSalaries([]);
      });

    setIsLoadingSalaries(false);
  };

  const recalculate = () => {
    const { roundoff_amount, withdrawal_deposit } = getValues();

    const total =
      parseFloat(`${orderAmount || 0}`) + parseFloat(`${roundoff_amount || 0}`);
    setTotalAmount(total);

    setLastTotalWithdrawal(
      parseFloat(`${totalWithdrawal || 0}`) -
        parseFloat(`${withdrawal_deposit || 0}`)
    );

    setLastTotalAmount(
      parseFloat(`${total || 0}`) - parseFloat(`${withdrawal_deposit || 0}`)
    );

    return true;
  };

  const handleGoToNextStep = async () => {
    setIsLoading(true);

    setValue("roundoff_amount", 0);
    setValue("withdrawal_deposit", 0);

    const isFromValid = await trigger("fromDate");
    const isToValid = await trigger("toDate");

    if (id && isFromValid && isToValid) {
      const data = getValues();

      try {
        await GET(`/v1/admin/salaries/employees/${id}/amount`, {
          fromDate: data.fromDate,
          toDate: data.toDate,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              setOrderAmount(response.data.data.total_amount);
              setTotalAmount(response.data.data.total_amount);
              setTotalHours(response.data.data.total_hours);
              setTotalWithdrawal(response.data.data.total_withdrawal);
              setLastTotalWithdrawal(response.data.data.total_withdrawal);
              setLastTotalAmount(response.data.data.total_amount);
              setRemainingPayment(response.data.data.remaining_payment || 0);
              setIsStep2Open(true);
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } catch (error) {
        toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
          ...(toastStyles as any),
        });
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    handleChangeEmployee(id || "0");
  }, [id]);

  return (
    <Dialog open onClose={handleCloseModal}>
      <DialogContent size="wide" className={dialogMobileContentClass}>
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            હિસાબ ની માહિતી
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className={cardClass}>
              <div className={cardContentFlushClass}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          colSpan={2}
                          className={cx(tableHeadCellClass, "text-center")}
                        >
                          હિસાબ બાકી હોય એવી હાજરી ની વિગતો
                        </TableHead>
                      </TableRow>
                      <TableRow>
                        <TableHead
                          className={cx(tableHeadCellClass, "text-center")}
                        >
                          તારીખ થી
                        </TableHead>
                        <TableHead
                          className={cx(tableHeadCellClass, "text-center")}
                        >
                          તારીખ સુધી
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    {isLoadingSalaries ? (
                      <TableLoadingRow colSpan={2} />
                    ) : pendingSalaries.length === 0 ? (
                      <TableEmptyRow
                        colSpan={2}
                        label="બાકી હિસાબ ની વિગતો મળી નથી"
                      />
                    ) : (
                      <TableBody>
                        {pendingSalaries.map((salary, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-center text-gray-500">
                              {moment(salary.from_date).format("DD-MM-YYYY")}
                            </TableCell>
                            <TableCell className="text-center text-gray-500">
                              {moment(salary.to_date).format("DD-MM-YYYY")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                </div>
              </div>
            </div>

            <div className={filterGridClass}>
              <div className="space-y-2">
                <label className={fieldLabelClass}>1 કલાક નો પગાર</label>

                <div className="relative">
                  <LuIndianRupee className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={employee?.amount || 0}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>
                  તારીખ થી <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <LuCalendar className={formFieldIconClass} />
                  <Input
                    type="date"
                    className={dialogInputClass(!!errors.fromDate)}
                    {...register("fromDate", { required: true })}
                    onChange={() => {
                      setIsStep2Open(false);
                      return true;
                    }}
                  />
                </div>

                {errors.fromDate && (
                  <p className={fieldErrorTextClass}>તારીખ સિલેક્ટ કરો</p>
                )}
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>
                  તારીખ સુધી <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <LuCalendar className={formFieldIconClass} />
                  <Input
                    type="date"
                    className={dialogInputClass(!!errors.toDate)}
                    {...register("toDate", { required: true })}
                    onChange={() => {
                      setIsStep2Open(false);
                      return true;
                    }}
                  />
                </div>

                {errors.toDate && (
                  <p className={fieldErrorTextClass}>તારીખ સિલેક્ટ કરો</p>
                )}
              </div>
            </div>

            {isStep2Open && (
              <>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={fieldLabelClass}>
                      હિસાબ કલાકો × રેટ
                    </label>

                    <div className="relative">
                      <LuClock className={formFieldIconClass} />
                      <Input
                        type="text"
                        readOnly
                        value={`${totalHours || 0} × ${employee?.amount || 0}`}
                        className={cx(dialogInputClass(), "bg-gray-100")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={fieldLabelClass}>હિસાબ અમાઉન્ટ</label>

                    <div className="relative">
                      <LuScale className={formFieldIconClass} />
                      <Input
                        type="text"
                        readOnly
                        value={orderAmount || 0}
                        className={cx(dialogInputClass(), "bg-gray-100")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={fieldLabelClass}>રાઉન્ડ ઑફ</label>

                    <div className="relative">
                      <LuIndianRupee className={formFieldIconClass} />
                      <Input
                        type="number"
                        step="any"
                        placeholder="રાઉન્ડ ઑફ દાખલ કરો"
                        className={dialogInputClass()}
                        {...register("roundoff_amount", { required: false })}
                        onKeyUp={recalculate}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={fieldLabelClass}>
                      હિસાબ ટોટલ અમાઉન્ટ
                    </label>

                    <div className="relative">
                      <LuScale className={formFieldIconClass} />
                      <Input
                        type="text"
                        readOnly
                        value={`${(totalAmount || 0).toFixed(2)}`}
                        className={cx(dialogInputClass(), "bg-gray-100")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={fieldLabelClass}>ટોટલ ઉપાડ</label>

                    <div className="relative">
                      <LuWallet className={formFieldIconClass} />
                      <Input
                        type="text"
                        readOnly
                        value={`${(totalWithdrawal || 0).toFixed(2)}`}
                        className={cx(dialogInputClass(), "bg-gray-100")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={fieldLabelClass}>કપાત ઉપાડ</label>

                    <div className="relative">
                      <LuIndianRupee className={formFieldIconClass} />
                      <Input
                        type="number"
                        step="any"
                        placeholder="કપાત ઉપાડ દાખલ કરો"
                        className={dialogInputClass()}
                        {...register("withdrawal_deposit", { required: false })}
                        onKeyUp={recalculate}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={cx(cardClass, "p-4")}>
                    <div className={summaryCardTitleClass}>
                      બાકી ઉપાડ અમાઉન્ટ
                    </div>
                    <div className={cx(summaryCardValueClass, "mt-2")}>
                      {formatAmount(lastTotalWithdrawal)}
                    </div>
                  </div>

                  <div className={cx(cardClass, "p-4")}>
                    <div className={summaryCardTitleClass}>
                      બાકી રહેતો પગાર
                    </div>
                    <div className={cx(summaryCardValueClass, "mt-2")}>
                      {formatAmount(remainingPayment)}
                    </div>
                  </div>

                  <div className={cx(cardClass, "p-4")}>
                    <div className={summaryCardTitleClass}>પગાર અમાઉન્ટ</div>
                    <div className={cx(summaryCardValueClass, "mt-2")}>
                      {formatAmount(
                        (lastTotalAmount || 0) + (remainingPayment || 0)
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseModal}
              disabled={isLoading}
            >
              બંધ કરો
            </Button>

            {isStep2Open ? (
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading && <span className={buttonSpinnerClass} />}
                આગળ વધો
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                className="flex-1"
                disabled={isLoading}
                onClick={handleGoToNextStep}
              >
                {isLoading && <span className={buttonSpinnerClass} />}
                આગળ વધો
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateForm;
